import React, { useState, useEffect } from 'react';
import { getLocale, useIntl } from 'umi';
import moment from 'moment';
import { Bubble } from 'tea-component';
import RangePicker from '@/pages/home/components/RangePicker';
import GenreSelector from '@intelligence/components/GenreSelectMiddleware';
import useGenreComponent from '@/pages/intelligence/hooks/useGenreComponent';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import Segment from './Segment';
import YearSelect from './YearSelect';
import Selector from './Selector';
import Text from './Text';
import { createFetchCascaderGenres } from '../../services';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { getMomentDateValue, checkDateRangeDiff, MAX_DIFF_YEARS } from '../../utils';
import { PER_TYPES, REVENUE_TYPES, REGION_MARKET_TYPES, GENRE_TYPES, REGION_OPTS, GENRE_OPTS } from '../../constants';
import type { IBaseProps, IRegionType, IMomentDateValue, IDateType } from '@/pages/intelligence/types/common';
import type { IFilter, IAction, IGenreType } from '../../types';
import './index.scss';
import { setShortcutOptionFun } from '@/pages/intelligence/components/Filter/FilterBase/_config';

interface IFilterProps extends IBaseProps {
  perType: string;
  revenueType: string;
  years: string[];
  regionOpts: any[];
  marketOpts: any[];
  genreOpts: any[];
  minDate?: string;
  maxDate?: string;
  dateTypes?: IDateType[];
  filter: IFilter;
  onFilterChange?: ({ type, payload }: IAction) => void;
  [key: string]: any;
}

// const TEXT = 'Genre Classification comes from IEGG and data comes from data.ai';
const TEXT = 'dbi_global_view_genre_classification_data_source_tips_mobile';
export default function Filter(props: IFilterProps) {
  const {
    isLoading,
    perType,
    revenueType,
    years,
    regionOpts,
    marketOpts,
    genreOpts,
    minDate,
    maxDate,
    dateTypes = ['month'],
    filter: filterOutter,
    onFilterChange,
  } = props;

  const { formatMessage } = useIntl();

  const locale = getLocale();

  const [dateRangeTipsShow, setDateRangeTipsShow] = useState(false);

  const [filter, setFilter] = useState<IFilter>(filterOutter);

  const cascaderGenresFetcher = createFetchCascaderGenres(filter?.genreType);

  const {
    genreRef,
    mainGenres,
    subGenres,

    handleGenresChange,
    // handleReset: handleGenresReset,
    // getGenresReqParams,
    // updateGenres2Store,
  } = useGenreComponent({
    mainGenres: filter?.mainGenres,
    subGenres: filter?.subGenres,
  });

  const disabledMonth = (date) => {
    // 选择范围在今天之前，且选择跨度不大于七天
    // const isAfterToday = date.isAfter(moment('2023-8-1'), 'month');
    const isBeforeToday = date.isBefore(
      moment('2015-1-1'),
      'month',
    );
    return !(isBeforeToday);
  };

  const rangePickerValue: IMomentDateValue = (() => getMomentDateValue(filter?.dateValue))();

  const handleFilterChange = (key: keyof IFilter, value: any, defaultValue: any) => {
    if (isLoading) return;
    const filterTemp = {
      ...filter,
      [key]: value || defaultValue,
    };
    setFilter(filterTemp);
    onFilterChange?.({ type: key, payload: value });
  };

  const getValidRangeValue = (dateValue: IMomentDateValue): string[] => {
    if (dateValue?.length !== 2) return [];
    let startDateTmp = dateValue[0];
    const endDateTmp = dateValue[1];
    const startDateInYear = startDateTmp.clone().startOf('year');
    const endDateInYear = endDateTmp.clone().startOf('year');
    const isRangeValid = checkDateRangeDiff(startDateInYear, endDateInYear);
    let newDateValue: string[] = [];
    // 时间范围跨度大于5年，重置为 结束时间的开始，往前推5个年
    if (!isRangeValid) {
      startDateTmp = moment(endDateTmp).subtract(MAX_DIFF_YEARS - 1, 'years');
      setDateRangeTipsShow(true);
      setTimeout(() => {
        setDateRangeTipsShow(false);
      }, 3000);
    }
    newDateValue = [startDateTmp?.format('YYYY-MM-DD'), endDateTmp?.format('YYYY-MM-DD')];
    return newDateValue;
  };

  const handleDatePickerChange = (dateValue: IMomentDateValue, dateType: IDateType) => {
    if (isLoading) return;
    // const dateValueStr = dateValue?.map((item) => item.format('YYYY-MM-DD'));
    const dateValueStr = getValidRangeValue(dateValue);
    const filterTemp = {
      ...filter,
      dateType,
      dateValue: dateValueStr,
    };
    setFilter(filterTemp);
    onFilterChange?.({ type: 'dateType', payload: dateType });
    onFilterChange?.({ type: 'dateValue', payload: dateValueStr });
  };

  const renderRegionMarketSelectUI = () => {
    if (filter?.regionType === REGION_MARKET_TYPES.REGION)
      return (
        <Selector
          key="regionSelector"
          className="search-item"
          name={formatMessage({ id: 'dbi_global_view_region' })}
          value={filter?.regions}
          onChange={(payload: string[]) => {
            handleFilterChange('regions', payload, []);
          }}
          opts={regionOpts}
        />
      );

    return (
      <Selector
        key="marketSelector"
        className="search-item"
        name={formatMessage({ id: 'dbi_global_view_markets' })}
        value={filter?.markets}
        onChange={(payload: string[]) => {
          handleFilterChange('markets', payload, []);
        }}
        opts={marketOpts}
      />
    );
  };

  const renderGenreSelectUI = () => {
    const isSubOrMainSub = filter?.genreType === GENRE_TYPES.SUB || filter?.genreType === GENRE_TYPES.MAIN_SUB;
    if (revenueType === REVENUE_TYPES.MOBILE && isSubOrMainSub)
      return (
        <GenreSelector
          key="genreSelector"
          // className="search-item"
          // @ts-ignore
          locale={locale}
          // @ts-ignore
          onConfirm={(payload: any) => {
            if (isLoading) return;
            const mainGenresTmp = payload?.mainGenres || [];
            const subGenresTmp = payload?.subGenres || [];
            const filterTemp = {
              ...filter,
              mainGenres: mainGenresTmp,
              subGenres: subGenresTmp,
            };
            setFilter(filterTemp);
            onFilterChange?.({ type: 'cascadeGenres', payload: {
              mainGenres: mainGenresTmp,
              subGenres: subGenresTmp
            }});
          }}
          ref={genreRef}
          // 只又IEGG的品类数据，所以source直接写死IEGG的source
          disableAnnie={true}
          source="iegg"
          // 只又IEGG的品类数据，所以source直接写死IEGG的source
          fetchAction={cascaderGenresFetcher}
          mainGenres={mainGenres}
          subGenres={subGenres}
          onChange={handleGenresChange}
        />
      );
    return (
      <Selector
        key="simpleGenreSelector"
        className="search-item"
        name={formatMessage({ id: 'dbi_global_view_genre' })}
        value={filter?.genres}
        onChange={(payload: string[]) => {
          handleFilterChange('genres', payload, []);
        }}
        opts={genreOpts}
      />
    );
  };

  useEffect(() => {
    if (!filterOutter) return;
    setFilter({
      ...filter,
      ...filterOutter,
    });
  }, [filterOutter]);

  return (
    <div className="global-view-filter-wrap">
      <Segment
        key="regionSegment"
        customClassname="segment-light search-item "
        value={filter?.regionType}
        onChange={(payload: IRegionType) => {
          handleFilterChange('regionType', payload, REGION_MARKET_TYPES.REGION);
        }}
        opts={REGION_OPTS}
      />
      {revenueType === REVENUE_TYPES.MOBILE || (
        <YearSelect
          className="search-item"
          years={years}
          value={filter.years}
          onChange={(payload: string[]) => {
            handleFilterChange('years', payload, []);
          }}
        />
      )}
      {revenueType === REVENUE_TYPES.MOBILE && (
        <>
          <Segment
            customClassname="segment-light search-item "
            key="genreSegment"
            value={filter?.genreType}
            onChange={(payload: IGenreType) => {
              if (isLoading) return;
              const filterTemp = {
                ...filter,
                genreType: payload,
                genres: [],
                mainGenres: [],
                subGenres: [],
              };
              setFilter(filterTemp);
              onFilterChange?.({ type: 'genreType', payload });
              onFilterChange?.({ type: 'genres', payload: [] });
              onFilterChange?.({ type: 'mainGenres', payload: [] });
              onFilterChange?.({ type: 'subGenres', payload: [] });

              if (perType === PER_TYPES.GENRE) {
                genreRef?.current?.reset();
              }
            }}
            opts={GENRE_OPTS}
          />
          <div className="search-item search-item-date dbi-search-item-date">
            <Bubble
              dark
              placementOffset={20}
              visible={dateRangeTipsShow}
              content="Can only query up to 5 years of data."
            >
              <RangePicker
                className="search-border"
                // 需要隐藏的header部分的按钮
                // hiddenHeaderBtns={['Latest Data']}
                /** 左侧栏时间粒度 */
                type={filter?.dateType}
                /** 时间值 */
                value={rangePickerValue}
                setShortcutOption={() => setShortcutOptionFun(filter?.dateType)}
                /** 日周月年的列表 */
                typeList={dateTypes}
                // startOfWeek={START_OF_WEEK}
                language={locale === LANGUAGE_CN ? 'cn' : 'en'}
                onChange={handleDatePickerChange}
                /** 是否有左侧的日周月 */
                showSelectType={dateTypes?.length > 1}
                /** 是否展示弹窗左侧时间筛选 */
                showShortcut={true}
                latestDate={maxDate}
                disabledMonth={disabledMonth}
                /** 日周月对应的默认值 */
                // defaultValue={}
              />
            </Bubble>
          </div>
        </>
      )}
      {perType === PER_TYPES.MARKET && renderRegionMarketSelectUI()}
      {perType === PER_TYPES.GENRE && renderGenreSelectUI()}
      {revenueType !== REVENUE_TYPES.MOBILE
        ? <Text className="search-item" />
        : <TipContext
          AWidth={555}
          placement="top-end"
          placementOffset={[18, 10]}
          tipText={formatMessage({ id: TEXT })}
          className={'tip-icon'}
        />
      }
    </div>
  );
}
